// Noise Analytics review: one scored file, flag by flag, in time order. Each flag plays from where its flag
// would go (a couple of seconds before the noise); Y flags it with its pre-written text, N dismisses it, and
// the next one plays. Every answer is saved as it is given.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { NoiseAnalytics, NoiseJudgement, NoiseKind, NoiseReviewFile } from "../api/types";
import { useMe } from "../lib/me";
import { ProjectHeader } from "../components/ProjectHeader";
import { NoiseView, clock, type Mark } from "../components/NoiseView";
import { NOISE_BASE } from "../components/NoiseAnalyticsDialog";
import { toast } from "../components/toast";
import { NoiseImportDialog } from "../components/NoiseImportDialog";
import { playExcerpt, playbackPosition, stopPlayback, wavExcerpt, type Excerpt } from "../lib/wavRange";

const HALF = 3;                       // seconds shown either side of the noise
const GRAINS = ["Coarse", "Medium", "Fine"] as const;
const NOUNS = ["Mouth noise", "Click", "Clink", "Lip smack"];     // keys 1–4; 0 goes back to "Noise"
const GRAIN_KEY = "noise-review-grain";
const PLAY_ID = "noise-review";

/** The flag as it will be imported (the server's phrases.flag_text, which words it quotes being its call). */
export function flagText(noun: string, kind: NoiseKind, quote: string) {
  if (!quote) return `${noun} #noise`;
  if (kind === "gap") return `${noun} in the gap after "${quote}" #noise`;
  if (kind === "gap-before") return `${noun} in the gap before "${quote}" #noise`;
  return `${noun} in "${quote}" #noise`;
}

/** A hand-edited text with its leading noun swapped, when it starts with one. */
function withNoun(text: string, noun: string) {
  for (const n of ["Noise", ...NOUNS].sort((a, b) => b.length - a.length)) {
    if (text === n || text.startsWith(n + " ")) return noun + text.slice(n.length);
  }
  return text;
}

interface Draft { noiseT: number; noun: string; kind: NoiseKind; quote: string; custom: string | null }
const textOf = (d: Draft) => d.custom ?? flagText(d.noun, d.kind, d.quote);

function readGrain() {
  try {
    const g = localStorage.getItem(GRAIN_KEY);
    return g === "0" || g === "1" || g === "2" ? Number(g) : 1;
  } catch {
    return 1;
  }
}

export function NoiseReview() {
  const { projectId = "", chapterId = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const key = useMemo(() => ["noise-file", projectId, chapterId], [projectId, chapterId]);
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<NoiseReviewFile>(`${NOISE_BASE}/${projectId}/files/${chapterId}`),
    enabled: !!me?.is_superadmin,
    refetchInterval: 20 * 60 * 1000,            // the recording's link expires; a fresh one before it does
    refetchOnWindowFocus: false,
  });
  const files = useQuery({
    queryKey: ["noise", projectId],
    queryFn: () => api<NoiseAnalytics>(`${NOISE_BASE}/${projectId}`),
    enabled: !!me?.is_superadmin,
  });
  const data = q.data;
  const pre = data?.pre_roll_s ?? 2;
  const [grain, setGrainState] = useState(readGrain);
  const flags = useMemo(() => (data?.flags ?? []).filter((f) => f.level_index <= grain).sort((a, b) => a.t - b.t), [data, grain]);
  const answers = useMemo(() => new Map((data?.judgements ?? []).filter((j) => j.source === "model").map((j) => [j.spot_t as number, j])), [data]);
  const added = useMemo(() => (data?.judgements ?? []).filter((j) => j.source === "added").sort((a, b) => a.noise_t - b.noise_t), [data]);
  const [idx, setIdx] = useState(0);
  const flag = flags[idx] ?? null;
  const answer = flag ? answers.get(flag.t) ?? null : null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [adding, setAdding] = useState<Draft | null>(null);
  const [clip, setClip] = useState<{ t: number; ex: Excerpt } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const textRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLInputElement>(null);
  const opened = useRef<string | null>(null);
  const played = useRef<number | null>(null);

  // arriving on a file: the first flag not yet answered
  useEffect(() => {
    if (!data || opened.current === chapterId) return;
    opened.current = chapterId;
    const i = flags.findIndex((f) => !answers.has(f.t));
    setIdx(i < 0 ? flags.length : i);
    setAdding(null);
    played.current = null;
  }, [data, chapterId, flags, answers]);

  const phrase = useCallback(async (t: number) => {
    try {
      return await api<{ kind: NoiseKind; quote: string }>(`${NOISE_BASE}/${projectId}/files/${chapterId}/phrase?t=${t.toFixed(3)}`);
    } catch {
      return { kind: "" as NoiseKind, quote: "" };
    }
  }, [projectId, chapterId]);

  // the flag's draft: its answer when it has one, else the model's spot with the default text
  useEffect(() => {
    if (!flag) { setDraft(null); return; }
    if (!answer) { setDraft({ noiseT: flag.t, noun: "Noise", kind: flag.kind, quote: flag.quote, custom: null }); return; }
    const d: Draft = { noiseT: answer.noise_t, noun: answer.noun, kind: flag.kind, quote: flag.quote, custom: answer.text };
    if (answer.text === flagText(answer.noun, flag.kind, flag.quote)) d.custom = null;
    setDraft(d);
    if (Math.abs(answer.noise_t - flag.t) > 0.0005) {             // it was moved: its words are the moved spot's
      void phrase(answer.noise_t).then((p) => setDraft((x) => x && x.noiseT === answer.noise_t
        ? { ...x, ...p, custom: x.custom === flagText(answer.noun, p.kind, p.quote) ? null : x.custom } : x));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag?.t, answer?.id, answer?.verdict]);

  // the excerpt around the flag, fixed while the spot is moved within it
  const center = flag?.t ?? null;
  useEffect(() => {
    if (!data || center == null) return;
    let live = true;
    setErr(null);
    wavExcerpt(data.audio_url, Math.max(0, center - HALF), center + HALF, `noise:${chapterId}`)
      .then((ex) => { if (live) setClip({ t: center, ex }); })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [data?.audio_url, center, chapterId]);        // eslint-disable-line react-hooks/exhaustive-deps
  const ex = clip && clip.t === center ? clip.ex : null;

  const play = useCallback((from?: number) => {
    if (!ex || !draft) return;
    const t = from ?? Math.max(0, draft.noiseT - pre);
    playExcerpt(ex, PLAY_ID, Math.max(0, t - ex.t0));
  }, [ex, draft, pre]);

  // each flag plays by itself on arrival
  useEffect(() => {
    if (ex && draft && center != null && played.current !== center) {
      played.current = center;
      play();
    }
  }, [ex, draft, center, play]);
  useEffect(() => () => stopPlayback(), []);

  const go = useCallback((i: number) => {
    stopPlayback();
    setAdding(null);
    setIdx(Math.max(0, Math.min(flags.length, i)));
  }, [flags.length]);

  function setGrain(g: number) {
    try { localStorage.setItem(GRAIN_KEY, String(g)); } catch { /* a convenience only */ }
    const list = (data?.flags ?? []).filter((f) => f.level_index <= g).sort((a, b) => a.t - b.t);
    const keep = flag ? list.findIndex((f) => f.t === flag.t) : -1;
    const next = keep >= 0 ? keep : list.findIndex((f) => !answers.has(f.t));
    setGrainState(g);
    setIdx(next < 0 ? list.length : next);
    if (keep < 0) played.current = null;
  }

  const store = (j: NoiseJudgement | null, removeId?: string) => {
    qc.setQueryData<NoiseReviewFile>(key, (d) => d && {
      ...d, judgements: [...d.judgements.filter((x) => x.id !== (j?.id ?? removeId)), ...(j ? [j] : [])],
    });
    qc.invalidateQueries({ queryKey: ["noise", projectId] });
  };

  async function judge(verdict: "yes" | "no") {
    if (!flag || !draft || !data) return;
    if (answer?.imported) { toast("This flag is already in proofing: change it in the report", "info"); return; }
    try {
      const j = await api<NoiseJudgement>(`${NOISE_BASE}/${projectId}/judgements`, {
        method: "PUT",
        body: JSON.stringify({ scan_id: data.scan.id, source: "model", spot_t: flag.t, noise_t: draft.noiseT, verdict,
                               noun: draft.noun, text: textOf(draft), p: flag.p, level_index: flag.level_index }),
      });
      store(j);
      go(idx + 1);
    } catch (e) {
      toast((e as Error).message || "Couldn't save that answer", "error");
    }
  }

  async function undo() {
    if (!answer) return;
    try {
      await api(`${NOISE_BASE}/${projectId}/judgements/${answer.id}`, { method: "DELETE" });
      store(null, answer.id);
    } catch (e) {
      toast((e as Error).message || "Couldn't take that answer back", "error");
    }
  }

  async function startAdd(t: number) {
    stopPlayback();
    const d: Draft = { noiseT: Math.max(0, t), noun: "Noise", kind: "", quote: "", custom: null };
    setAdding(d);
    const p = await phrase(d.noiseT);
    setAdding((x) => x && x.noiseT === d.noiseT ? { ...x, ...p } : x);
    setTimeout(() => addRef.current?.focus(), 0);
  }

  async function saveAdded() {
    if (!adding || !data) return;
    try {
      const j = await api<NoiseJudgement>(`${NOISE_BASE}/${projectId}/judgements`, {
        method: "PUT",
        body: JSON.stringify({ scan_id: data.scan.id, source: "added", noise_t: adding.noiseT, verdict: "yes",
                               noun: adding.noun, text: textOf(adding) }),
      });
      store(j);
      setAdding(null);
    } catch (e) {
      toast((e as Error).message || "Couldn't add that flag", "error");
    }
  }

  async function removeAdded(j: NoiseJudgement) {
    try {
      await api(`${NOISE_BASE}/${projectId}/judgements/${j.id}`, { method: "DELETE" });
      store(null, j.id);
    } catch (e) {
      toast((e as Error).message || "Couldn't remove that flag", "error");
    }
  }

  function pick(t: number, shift: boolean) {
    if (shift) { void startAdd(t); return; }
    if (adding) {                                    // placing the flag being added
      setAdding({ ...adding, noiseT: t });
      void phrase(t).then((p) => setAdding((x) => x && x.noiseT === t ? { ...x, ...p } : x));
      return;
    }
    if (!draft || answer?.imported) return;
    setDraft({ ...draft, noiseT: t });
    void phrase(t).then((p) => setDraft((x) => x && x.noiseT === t ? { ...x, ...p } : x));
    if (ex) playExcerpt(ex, PLAY_ID, Math.max(0, t - pre - ex.t0));        // hear it from its new flag
  }

  function setNoun(noun: string) {
    const upd = (d: Draft): Draft => ({ ...d, noun, custom: d.custom == null ? null : withNoun(d.custom, noun) });
    if (adding) setAdding(upd(adding));
    else if (draft && !answer?.imported) setDraft(upd(draft));
  }

  // the keys
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || importing) return;     // not behind the import dialog
      const el = e.target as HTMLElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      const k = e.key.toLowerCase();
      if (k === " ") { e.preventDefault(); play(); return; }
      if (k === "y") { void (adding ? saveAdded() : judge("yes")); return; }
      if (k === "n") { if (!adding) void judge("no"); return; }
      if (k === "arrowright") { e.preventDefault(); go(idx + 1); return; }
      if (k === "arrowleft") { e.preventDefault(); go(idx - 1); return; }
      if (k === "e") { e.preventDefault(); (adding ? addRef : textRef).current?.focus(); return; }
      if (k === "p") {
        const pos = playbackPosition(PLAY_ID);
        if (pos != null && ex) void startAdd(ex.t0 + pos);
        else toast("Play first (Space), then press P where you hear it; or Shift+click the picture", "info");
        return;
      }
      if (k === "escape") { if (adding) setAdding(null); else stopPlayback(); return; }
      if (k === "0") { setNoun("Noise"); return; }
      if (["1", "2", "3", "4"].includes(k)) { setNoun(NOUNS[Number(k) - 1]); return; }
      if (k === "u") { const i = flags.findIndex((f) => !answers.has(f.t)); if (i >= 0) go(i); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (me && !me.is_superadmin) return <Navigate to={`/projects/${projectId}`} replace />;

  const reviewable = (files.data?.files ?? []).filter((f) => f.scan?.status === "done" && !f.scan.stale);
  const here = reviewable.findIndex((f) => f.chapter_id === chapterId);
  const prevFile = here > 0 ? reviewable[here - 1] : null;
  const nextFile = here >= 0 && here + 1 < reviewable.length ? reviewable[here + 1] : null;
  const judgedHere = flags.filter((f) => answers.has(f.t)).length;
  const counts = [0, 1, 2].map((g) => (data?.flags ?? []).filter((f) => f.level_index <= g).length);
  const marks: Mark[] = [];
  if (draft && flag) {
    marks.push({ t: draft.noiseT, kind: "noise", label: "noise" });
    marks.push({ t: Math.max(0, draft.noiseT - pre), kind: "flag", label: "flag" });
  }
  for (const a of added) marks.push({ t: a.noise_t, kind: "added", label: "yours" });
  if (adding) marks.push({ t: adding.noiseT, kind: "added", label: "new" });
  const state = !answer ? ["", "Not judged yet"] : answer.imported ? ["imported", "In proofing"]
    : answer.verdict === "yes" ? ["yes", "Flagged"] : ["no", "Dismissed"];

  return (
    <>
      <ProjectHeader projectId={projectId} name={files.data?.project.name ?? "Project"} active="noise"
        right={<button className="btn primary sm" onClick={() => { stopPlayback(); setImporting(true); }}>Import into proofing</button>} />
      {importing && <NoiseImportDialog projectId={projectId} onClose={() => setImporting(false)} />}
      <div className="page nr-page">
        <div className="nr-head">
          <Link to={`/projects/${projectId}/noise`} className="lab-back">Files</Link>
          <div className="nr-file mono">{data?.name ?? "…"}</div>
          <div className="nr-grains" role="group" aria-label="Grain">
            {GRAINS.map((g, i) => (
              <button key={g} className={`btn sm${grain === i ? " primary" : " ghost"}`} onClick={() => setGrain(i)}>
                {g} <span className="nr-count">{counts[i]}</span>
              </button>
            ))}
          </div>
          <span className="muted nr-progress">{judgedHere} of {flags.length} judged</span>
          <span className="nr-filenav">
            <button className="btn ghost sm" disabled={!prevFile} onClick={() => prevFile && nav(`/projects/${projectId}/noise/${prevFile.chapter_id}`)}>‹ Previous file</button>
            <button className="btn ghost sm" disabled={!nextFile} onClick={() => nextFile && nav(`/projects/${projectId}/noise/${nextFile.chapter_id}`)}>Next file ›</button>
          </span>
        </div>

        {q.isError ? (
          <div className="lab-error">{(q.error as Error).message}</div>
        ) : !data ? (
          <div className="muted">Loading…</div>
        ) : (
          <>
            {!data.has_words && <div className="lab-error">This file has no words from proofing, so its flags can't quote any.</div>}
            {flag && draft ? (
              <section className="card nr-card">
                <div className="nr-top">
                  <b>Flag {idx + 1} of {flags.length}</b>
                  <span className="muted">
                    at {clock(flag.t, 2)} · {GRAINS[flag.level_index]} · {Math.round(flag.p * 1000) / 10}% sure
                    {flag.severity_db != null && ` · ${Math.abs(Math.round(flag.severity_db))} dB below the voice`}
                  </span>
                  <span className={`nr-state ${state[0]}`}>{state[1]}</span>
                  {answer && !answer.imported && <button className="btn ghost sm" onClick={undo}>Undo answer</button>}
                </div>
                {err ? <div className="lab-error">{err}</div> : <NoiseView excerpt={ex} playId={PLAY_ID} marks={marks} onPick={pick} />}
                <div className="nr-hint muted">Click the picture to put the noise where you hear it; Shift+click to add a flag of your own there.</div>
                {!adding ? (
                  <>
                    <div className="nr-nouns" role="group" aria-label="What it is">
                      {["Noise", ...NOUNS].map((n, i) => (
                        <button key={n} className={`btn sm${draft.noun === n ? " primary" : " ghost"}`} onClick={() => setNoun(n)} disabled={answer?.imported}>
                          <kbd>{i}</kbd> {n}
                        </button>
                      ))}
                    </div>
                    <div className="nr-textrow">
                      <input ref={textRef} className="input mono nr-text" value={textOf(draft)} disabled={answer?.imported}
                             onChange={(e) => setDraft({ ...draft, custom: e.target.value })}
                             onKeyDown={(e) => {
                               if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); void judge("yes"); }
                               if (e.key === "Escape") (e.target as HTMLInputElement).blur();
                             }} aria-label="Flag text" />
                      <span className="muted nr-at">flag at {clock(Math.max(0, draft.noiseT - pre), 2)}</span>
                    </div>
                    <div className="nr-actions">
                      <button className="btn primary" onClick={() => judge("yes")} disabled={answer?.imported}><kbd>Y</kbd> Flag it</button>
                      <button className="btn" onClick={() => judge("no")} disabled={answer?.imported}><kbd>N</kbd> Dismiss</button>
                      <button className="btn ghost" onClick={() => play()}><kbd>Space</kbd> Play again</button>
                      <button className="btn ghost" onClick={() => go(idx - 1)} disabled={idx === 0}><kbd>←</kbd> Back</button>
                      <button className="btn ghost" onClick={() => go(idx + 1)}><kbd>→</kbd> Skip</button>
                    </div>
                  </>
                ) : (
                  <div className="nr-adding">
                    <div className="nr-nouns" role="group" aria-label="What it is">
                      {["Noise", ...NOUNS].map((n, i) => (
                        <button key={n} className={`btn sm${adding.noun === n ? " primary" : " ghost"}`} onClick={() => setNoun(n)}><kbd>{i}</kbd> {n}</button>
                      ))}
                    </div>
                    <div className="nr-textrow">
                      <input ref={addRef} className="input mono nr-text" value={textOf(adding)}
                             onChange={(e) => setAdding({ ...adding, custom: e.target.value })}
                             onKeyDown={(e) => {
                               if (e.key === "Enter") { e.preventDefault(); void saveAdded(); }
                               if (e.key === "Escape") setAdding(null);
                             }} aria-label="Your flag's text" />
                      <span className="muted nr-at">your flag at {clock(Math.max(0, adding.noiseT - pre), 2)}</span>
                    </div>
                    <div className="nr-actions">
                      <button className="btn primary" onClick={saveAdded}><kbd>Enter</kbd> Add this flag</button>
                      <button className="btn ghost" onClick={() => setAdding(null)}><kbd>Esc</kbd> Cancel</button>
                      <span className="muted">Type over the text for anything else, with its own hashtag (#defect, #misread…).</span>
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <section className="card nr-card nr-done">
                <b>{flags.length ? `All ${flags.length} ${GRAINS[grain]} flags in this file are judged.` : `No ${GRAINS[grain]} flags in this file.`}</b>
                <div className="nr-actions">
                  {flags.length > 0 && <button className="btn ghost" onClick={() => go(0)}>Go through them again</button>}
                  {nextFile && <button className="btn primary" onClick={() => nav(`/projects/${projectId}/noise/${nextFile.chapter_id}`)}>Next file: {nextFile.name} ›</button>}
                </div>
              </section>
            )}

            {added.length > 0 && (
              <section className="card nr-added">
                <div className="card-head"><div className="card-head-title">Your own flags in this file ({added.length})</div></div>
                <ul>
                  {added.map((a) => (
                    <li key={a.id}>
                      <span className="mono nr-at">{clock(a.flag_t, 2)}</span>
                      <span className="nr-addedtext">{a.text}</span>
                      {a.imported ? <span className="muted">in proofing</span>
                        : <button className="btn ghost sm lab-danger" onClick={() => removeAdded(a)}>Remove</button>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="nr-keys muted">
              <kbd>Y</kbd> flag it · <kbd>N</kbd> dismiss · <kbd>Space</kbd> play again · <kbd>←</kbd> <kbd>→</kbd> back / skip ·
              <kbd>0</kbd>–<kbd>4</kbd> Noise, Mouth noise, Click, Clink, Lip smack · <kbd>E</kbd> edit the text ·
              <kbd>P</kbd> add your own flag at the playhead · <kbd>U</kbd> next unjudged
            </div>
          </>
        )}
      </div>
    </>
  );
}
